/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Redux state for managing videochat window, participants, and connection status.
 */
export interface VideochatState {
  isOpen: boolean;
  isFloating: boolean;
  isMinimized: boolean;
  position: { x: number; y: number };
  appointmentId: string | null;
  patientName: string | null;
  providerName: string | null;
  callState: string;
  isActive: boolean;
  participants: string[];
  participantCount: number;
}

const initialState: VideochatState = {
  isOpen: false,
  isFloating: false,
  isMinimized: false,
  position: { x: 100, y: 100 },
  appointmentId: null,
  patientName: null,
  providerName: null,
  callState: 'None',
  isActive: false,
  participants: [],
  participantCount: 0
};

/**
 * Payload for starting a videochat session with patient and provider information.
 */
export interface StartVideochatPayload {
  appointmentId: string;
  patientName: string;
  providerName: string;
  startFloating?: boolean;
}

const videochatSlice = createSlice({
  name: 'videochat',
  initialState,
  reducers: {
    /**
     * Starts a videochat session with patient and provider information.
     * Opens window in floating mode and sets call to active state.
     */
    startVideochat: (state, action: PayloadAction<StartVideochatPayload>) => {
      state.isOpen = true;
      state.isFloating = true;
      state.isMinimized = false;
      state.appointmentId = action.payload.appointmentId;
      state.patientName = action.payload.patientName;
      state.providerName = action.payload.providerName;
      state.callState = 'None';
      state.isActive = true;
    },

    /**
     * Closes videochat session and resets all state to initial values.
     * Clears appointment, participant, and connection data.
     */
    closeVideochat: (state) => {
      state.isOpen = false;
      state.isFloating = false;
      state.isMinimized = false;
      state.appointmentId = null;
      state.patientName = null;
      state.providerName = null;
      state.callState = 'None';
      state.isActive = false;
    },

    toggleFloatingMode: (state) => {
      state.isFloating = !state.isFloating;
      if (state.isFloating) {
        state.isMinimized = false;
      }
    },

    toggleMinimized: (state) => {
      if (state.isFloating) {
        state.isMinimized = !state.isMinimized;
      }
    },

    updatePosition: (state, action: PayloadAction<{ x: number; y: number }>) => {
      state.position = action.payload;
    },

    updateCallState: (state, action: PayloadAction<string>) => {
      state.callState = action.payload;
    },

    setCallActive: (state, action: PayloadAction<boolean>) => {
      state.isActive = action.payload;
    },

    hideVideochat: (state) => {
      state.isOpen = false;
    },

    showVideochat: (state) => {
      if (state.isActive) {
        state.isOpen = true;
      }
    },

    /**
     * Updates the complete list of call participants and recalculates count.
     */
    updateParticipants: (state, action: PayloadAction<string[]>) => {
      state.participants = action.payload;
      state.participantCount = state.participants.length + (state.callState === 'Connected' ? 1 : 0);
    },

    addParticipant: (state, action: PayloadAction<string>) => {
      if (!state.participants.includes(action.payload)) {
        state.participants.push(action.payload);
        state.participantCount = state.participants.length + (state.callState === 'Connected' ? 1 : 0);
      }
    },

    removeParticipant: (state, action: PayloadAction<string>) => {
      state.participants = state.participants.filter(p => p !== action.payload);
      state.participantCount = state.participants.length + (state.callState === 'Connected' ? 1 : 0);
    },

    clearParticipants: (state) => {
      state.participants = [];
      state.participantCount = 0;
    }
  }
});

export const {
  startVideochat,
  closeVideochat,
  toggleFloatingMode,
  toggleMinimized,
  updatePosition,
  updateCallState,
  setCallActive,
  hideVideochat,
  showVideochat,
  updateParticipants,
  addParticipant,
  removeParticipant,
  clearParticipants
} = videochatSlice.actions;

export default videochatSlice;

